import React, { useState, useEffect, useCallback } from 'react';
import './SuperAdminPanel.css';

const API_HEADERS = (user) => ({
  'Content-Type': 'application/json',
  'X-Admin-User': user.username,
  'X-Admin-Pass': user._adminPass || ''
});

// Master Manager Panel – accessible only when currentUser.role === 'master_manager'
const SuperAdminPanel = ({ currentUser, onLogout, onEnterSite }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteLogo, setNewSiteLogo] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [editName, setEditName] = useState('');
  const [editLogo, setEditLogo] = useState('');
  const [siteUsers, setSiteUsers] = useState(null);
  const [viewingUsersFor, setViewingUsersFor] = useState(null);
  const [adminUsername, setAdminUsername] = useState(currentUser?.username || '');
  const [adminPass, setAdminPass] = useState(currentUser?._adminPass || '');
  const [accountName, setAccountName] = useState('');
  const [accountUsername, setAccountUsername] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const isEmergencyAdmin = currentUser?.id === 'emergency_master';

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Admin-User': adminUsername,
    'X-Admin-Pass': adminPass
  }), [adminUsername, adminPass]);

  const loadSites = useCallback(() => {
    if (!adminPass || !adminUsername) {
      return;
    }
    fetch('/api/master-admin/sites', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { if (data.success) setSites(data.data || []); })
      .catch(() => showMsg('Failed to load sites', 'error'));
  }, [authHeaders, adminPass, adminUsername]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Load current master manager account details into the form on mount
  useEffect(() => {
    fetch('/api/master-admin/account', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setAccountName(data.data.name || 'Master Manager');
          setAccountUsername(data.data.username || '');
          setAccountPassword('');
        } else if (!isEmergencyAdmin) {
          setAccountName(currentUser?.name || 'Master Manager');
          setAccountUsername(currentUser?.username || '');
          setAccountPassword(currentUser?._adminPass || '');
        }
      })
      .catch(() => {
        if (!isEmergencyAdmin) {
          setAccountName(currentUser?.name || 'Master Manager');
          setAccountUsername(currentUser?.username || '');
          setAccountPassword(currentUser?._adminPass || '');
        }
      });
  }, []);



  const handleUpdateAccount = (e) => {
    e.preventDefault();

    if (!accountUsername.trim() || !accountPassword.trim()) {
      showMsg('❌ Username and password are required.', 'error');
      return;
    }

    setSavingAccount(true);
    fetch('/api/master-admin/account', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({
        name: accountName.trim() || 'Master Manager',
        username: accountUsername.trim(),
        password: accountPassword.trim(),
      })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setAdminUsername(accountUsername.trim());
          setAdminPass(accountPassword.trim());
          showMsg('✅ Master Manager username/password updated successfully.');
        } else {
          showMsg('❌ ' + (data.message || 'Failed to update account'), 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to update account', 'error'))
      .finally(() => setSavingAccount(false));
  };

  const handleAddSite = (e) => {
    e.preventDefault();
    if (!newSiteName.trim()) return;
    fetch('/api/master-admin/sites', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ name: newSiteName.trim(), logo_url: newSiteLogo.trim() || undefined })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          showMsg(`✅ Hospital "${data.data.name}" added! Default admin: ${data.data.default_admin_username} / ${data.data.default_admin_password}`);
          setNewSiteName('');
          setNewSiteLogo('');
          setShowAddForm(false);
          loadSites();
        } else {
          showMsg('❌ ' + data.message, 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to add site', 'error'));
  };

  const handleToggleActive = (site) => {
    if (site.id === 'hosp_001') {
      showMsg('❌ The default site cannot be disabled.', 'error');
      return;
    }
    fetch(`/api/master-admin/sites/${site.id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ is_active: !site.is_active })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          showMsg(`✅ "${site.name}" is now ${data.data.is_active ? 'active' : 'disabled'}.`);
          loadSites();
        } else {
          showMsg('❌ ' + data.message, 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to update site', 'error'));
  };

  const startEdit = (site) => {
    setEditingSite(site.id);
    setEditName(site.name);
    setEditLogo(site.logo_url || '');
  };

  const handleSaveEdit = (siteId) => {
    fetch(`/api/master-admin/sites/${siteId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ name: editName.trim(), logo_url: editLogo.trim() })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          showMsg('✅ Site updated.');
          setEditingSite(null);
          loadSites();
        } else {
          showMsg('❌ ' + data.message, 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to update', 'error'));
  };

  const handleViewUsers = (site) => {
    if (viewingUsersFor === site.id) {
      setViewingUsersFor(null);
      setSiteUsers(null);
      return;
    }
    fetch(`/api/master-admin/sites/${site.id}/users`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setViewingUsersFor(site.id);
          setSiteUsers(data.data || []);
        } else {
          showMsg('❌ ' + data.message, 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to load users', 'error'));
  };

  const handleEnterSite = (site) => {
    if (!onEnterSite) return;
    fetch('/api/master-admin/login-site', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ site_id: site.id })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          onEnterSite(data.user);
        } else {
          showMsg('❌ ' + data.message, 'error');
        }
      })
      .catch(() => showMsg('❌ Failed to enter site', 'error'));
  };

  return (
    <div className="sa-wrapper">
      <div className="sa-header">
        <div>
          <h1>Master Manager Panel</h1>
          <p>Hello BME — Multi-Site Hospital Administration</p>
        </div>
        <div className="sa-header-actions">
          <span className="sa-user-badge">👤 {currentUser.name}</span>
          <button className="sa-btn sa-btn-danger" onClick={onLogout}>Logout</button>
        </div>
      </div>

      <div className="sa-content">
        {message.text && (
          <div className={`sa-message ${message.type}`}>{message.text}</div>
        )}

        <div className="sa-section-header">
          <h2>🏥 Registered Hospitals ({sites.length})</h2>
          <button
            className="sa-btn sa-btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? '✖ Cancel' : '➕ Add Hospital'}
          </button>
        </div>

        {showAddForm && (
          <div className="sa-add-form">
            <h3>Add New Hospital</h3>
            <form onSubmit={handleAddSite}>
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>Hospital Name *</label>
                  <input
                    type="text"
                    value={newSiteName}
                    onChange={e => setNewSiteName(e.target.value)}
                    placeholder="e.g. Apollo Hospital Ahmedabad"
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Logo URL (optional)</label>
                  <input
                    type="text"
                    value={newSiteLogo}
                    onChange={e => setNewSiteLogo(e.target.value)}
                    placeholder="/logos/apollo.png"
                  />
                </div>
              </div>
              <button type="submit" className="sa-btn sa-btn-success">✅ Create Hospital</button>
            </form>
          </div>
        )}

        <div className="sa-sites-grid">
          {sites.map(site => (
            <div key={site.id} className={`sa-site-card ${!site.is_active ? 'inactive' : ''}`}>
              <div className="sa-site-top">
                {site.logo_url && (
                  <img
                    src={site.logo_url}
                    alt={site.name}
                    className="sa-site-logo"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="sa-site-info">
                  {editingSite === site.id ? (
                    <div>
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="sa-inline-input"
                      />
                      <input
                        type="text"
                        value={editLogo}
                        onChange={e => setEditLogo(e.target.value)}
                        className="sa-inline-input"
                        placeholder="Logo URL"
                      />
                    </div>
                  ) : (
                    <>
                      <h3>{site.name}</h3>
                      <span className="sa-site-id">ID: {site.id}</span>
                    </>
                  )}
                  <span className={`sa-status-badge ${site.is_active ? 'active' : 'disabled'}`}>
                    {site.is_active ? '✅ Active' : '⛔ Disabled'}
                  </span>
                </div>
              </div>

              <div className="sa-site-stats">
                <span>👥 {site.user_count ?? '—'} users</span>
                <span>📅 {site.created_at ? new Date(site.created_at).toLocaleDateString() : '—'}</span>
              </div>

              <div className="sa-site-actions">
                {editingSite === site.id ? (
                  <>
                    <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleSaveEdit(site.id)}>💾 Save</button>
                    <button className="sa-btn sa-btn-secondary sa-btn-sm" onClick={() => setEditingSite(null)}>✖ Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="sa-btn sa-btn-secondary sa-btn-sm" onClick={() => startEdit(site)}>✏️ Edit</button>
                    <button
                      className={`sa-btn sa-btn-sm ${site.is_active ? 'sa-btn-warning' : 'sa-btn-success'}`}
                      onClick={() => handleToggleActive(site)}
                      disabled={site.id === 'hosp_001'}
                    >
                      {site.is_active ? '⛔ Disable' : '✅ Enable'}
                    </button>
                    <button className="sa-btn sa-btn-info sa-btn-sm" onClick={() => handleViewUsers(site)}>
                      {viewingUsersFor === site.id ? '🔼 Hide Users' : '👥 View Users'}
                    </button>
                    {site.is_active && (
                      <button
                        className="sa-btn sa-btn-primary sa-btn-sm"
                        onClick={() => handleEnterSite(site)}
                        title={`Enter ${site.name} as admin`}
                      >
                        ➡ Enter Site
                      </button>
                    )}
                  </>
                )}
              </div>

              {viewingUsersFor === site.id && siteUsers && (
                <div className="sa-users-list">
                  <h4>Users in "{site.name}"</h4>
                  {siteUsers.length === 0 ? (
                    <p style={{ color: '#999' }}>No users found.</p>
                  ) : (
                    <table className="sa-users-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Username</th>
                          <th>Name</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {siteUsers.map((u, i) => (
                          <tr key={u.id}>
                            <td>{i + 1}</td>
                            <td>{u.username}</td>
                            <td>{u.name}</td>
                            <td><span className={`sa-role-badge ${u.role}`}>{u.role}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="sa-footer-note">
          <p>💡 <strong>Adding a new hospital</strong> automatically creates an isolated user database and a default admin account. Share the credentials with the Site Admin.</p>
          <p>💡 <strong>Disabling a hospital</strong> prevents all logins for that site without deleting any data.</p>
        </div>

        <div className="sa-add-form" style={{ marginTop: '28px' }}>
          {isEmergencyAdmin && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: '#856404' }}>
              ⚠️ <strong>Emergency Access Mode</strong> — You are logged in via the emergency master key (ZBME).<br />
              Use the form below to reset the regular <strong>Master Manager</strong> login credentials.
            </div>
          )}
          <h3>🔐 {isEmergencyAdmin ? 'Reset Master Manager Login' : 'Master Manager Account'}</h3>
          <form onSubmit={handleUpdateAccount}>
            <div className="sa-form-row">
              <div className="sa-form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={accountName}
                  onChange={e => setAccountName(e.target.value)}
                  placeholder="Master Manager"
                />
              </div>
              <div className="sa-form-group">
                <label>Username (ID)</label>
                <input
                  type="text"
                  value={accountUsername}
                  onChange={e => setAccountUsername(e.target.value)}
                  required
                  placeholder="e.g. parth"
                />
              </div>
            </div>
            <div className="sa-form-row">
              <div className="sa-form-group">
                <label>{isEmergencyAdmin ? 'Set New Password' : 'New Password'}</label>
                <input
                  type="password"
                  value={accountPassword}
                  onChange={e => setAccountPassword(e.target.value)}
                  required
                  placeholder={isEmergencyAdmin ? 'Enter new password for Master Manager' : ''}
                />
              </div>
              <div className="sa-form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button type="submit" className="sa-btn sa-btn-primary" disabled={savingAccount || loading}>
                  {savingAccount ? 'Saving...' : (isEmergencyAdmin ? '🔑 Reset Master Manager Login' : 'Save Master Manager Credentials')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
